import { type Inspection } from '@lib/spproject/types';
import { Box } from '@mui/material';
import {
  type ReportCheckpointsSegment, type ReportChrome, type ReportChromeSlot, type ReportSummarySegment,
} from '@store/api/reportDesignEndpoints';
import { Fragment } from 'react';
import { SIDES, STATUSES } from '../options';
import { capitals, palette, type Print } from './print';
import { answerStyles, FLEX_ALIGN, pageBreakStyles, reportStyles as styles, viewStyles } from './print.styles';
import { pointNotice, type SamplePoint, type SampleStep } from './sample';

/*
 * The parts of a report that are not blocks, as `DesignReport.tsx` prints them, in HTML and
 * over the sample inspection: the header and footer strip, the summary in each of its
 * templates, and the checkpoints in each of theirs. Templates and never blocks: they are set
 * up in the inspector and drawn here, and nothing in this file can be dragged.
 */

type NoticeType = Inspection.NoticeType;

const noticeLabel = (notice: NoticeType): string => STATUSES.find(({ value }) => value === notice)?.label ?? 'Common_NotChecked';

/** The number a point is listed under: its own, whatever a status filter left out before it. */
const numberOf = (point: SamplePoint): string => String(point.number).padStart(2, '0');

const StatusPill: React.FC<{ notice: NoticeType; print: Print }> = ({ notice, print }) => (
  <Box sx={styles.pill} style={{ backgroundColor: palette.statusTint[notice] }}>
    <Box sx={styles.pillText} style={{ color: palette.status[notice] }}>{print.t(noticeLabel(notice))}</Box>
  </Box>
);

// ---- Header and footer ----

/** Where a sheet stands in the report, for a slot that prints page numbers. */
export interface PageNumber {
  number: number;
  of: number;
}

const slotContent = (slot: ReportChromeSlot | undefined, print: Print, page: PageNumber): React.ReactNode => {
  switch (slot?.type) {
    case 'logo':
      return print.logo ? <Box component="img" alt="" draggable={false} src={print.logo} sx={styles.chromeLogo} /> : null;
    case 'brandName':
      return <Box sx={styles.chromeBrand}>{print.brandName}</Box>;
    case 'text':
      return <Box sx={styles.chromeText}>{print.text(slot.text)?.text}</Box>;
    case 'date':
      return <Box sx={styles.chromeText}>{print.formatDate(print.generatedAt)}</Box>;
    case 'pageNumbers':
      return <Box sx={styles.chromeText}>{`${page.number} / ${page.of}`}</Box>;
    default:
      return null;
  }
};

/**
 * A header or a footer: three slots on a strip. Nothing where the strip is switched off, as
 * on the report.
 */
export const ChromeStrip: React.FC<{ chrome: ReportChrome; edge: 'header' | 'footer'; print: Print; page: PageNumber }> = ({
  chrome, edge, print, page,
}) => (chrome.enabled ? (
  <Box sx={styles[edge]}>
    {SIDES.map(({ side }) => (
      <Box key={side} sx={styles.chromeSlot} style={{ justifyContent: FLEX_ALIGN[side] }}>
        {slotContent(chrome[side], print, page)}
      </Box>
    ))}
  </Box>
) : null);

// ---- Summary ----

/** The inspection in one sentence (`verdictLine` in `verdict.ts`). */
const verdictText = ({ summary, t }: Print): string => {
  switch (summary.verdict) {
    case 'attention': return t('Report_Verdict_Attention', [summary.findings, summary.countedPoints]);
    case 'clear': return t('Report_Verdict_Clear');
    case 'incomplete': return t('Report_Verdict_Incomplete', [summary.countedPoints - summary.finishedPoints, summary.countedPoints]);
    default: return t('Report_Verdict_Empty');
  }
};

/**
 * The summary in its template. Every number counts checkpoints, as on the report, and only
 * the dashboard reads the four switches. Nothing for `none`.
 */
export const SummaryView: React.FC<{ segment: ReportSummarySegment; print: Print }> = ({ segment, print }) => {
  if (segment.template === 'none') return null;

  const { noticeCounts, countedPoints, completionPercent, verdictNotice } = print.summary;

  const title = <Box sx={styles.sectionTitle}>{capitals(print.text(segment.title) ?? print.translate('Common_Summary'))}</Box>;

  const legend = (
    <Box sx={styles.legend}>
      {STATUSES.map(({ value, label }) => (
        <Box key={value} sx={styles.legendItem}>
          <Box sx={styles.legendDot} style={{ backgroundColor: palette.status[value] }} />
          <Box sx={styles.legendText}>{`${print.t(label)} ${noticeCounts[value]}`}</Box>
        </Box>
      ))}
    </Box>
  );

  if (segment.template === 'minimal') {
    return (
      <Box sx={viewStyles}>
        {title}
        <Box sx={styles.completionRow}>
          <Box sx={styles.completionFigure}>{`${completionPercent}%`}</Box>
          {legend}
        </Box>
      </Box>
    );
  }

  if (segment.template === 'table') {
    return (
      <Box sx={viewStyles}>
        {title}
        <Box sx={styles.tableHeader}>
          <Box sx={[styles.tableHeaderText, styles.summaryColumn]}>{capitals(print.translate('Common_Completion'))}</Box>
          {STATUSES.map(({ value, label }) => (
            <Box key={value} sx={[styles.tableHeaderText, styles.summaryColumn]}>{capitals(print.translate(label))}</Box>
          ))}
        </Box>
        <Box sx={styles.tableRow}>
          <Box sx={[styles.cellText, styles.summaryColumn]}>{`${completionPercent}%`}</Box>
          {STATUSES.map(({ value }) => (
            <Box key={value} sx={[styles.cellText, styles.summaryColumn]} style={{ color: palette.status[value] }}>
              {noticeCounts[value]}
            </Box>
          ))}
        </Box>
        <Box sx={styles.tableEnd} />
      </Box>
    );
  }

  return (
    <Box sx={viewStyles}>
      {title}
      {(segment.showVerdict ?? true) && (
        <Box sx={styles.verdictPanel} style={{ backgroundColor: palette.statusTint[verdictNotice] }}>
          <Box sx={styles.verdictText} style={{ color: palette.status[verdictNotice] }}>{verdictText(print)}</Box>
        </Box>
      )}
      <Box sx={styles.completionRow}>
        {(segment.showCompletion ?? true) && (
          <>
            <Box sx={styles.completionNumber}>{completionPercent}</Box>
            <Box sx={styles.completionUnit}>%</Box>
          </>
        )}
        {(segment.showBar ?? true) && countedPoints > 0 && (
          <Box sx={styles.progressTrack}>
            {STATUSES.filter(({ value }) => noticeCounts[value] > 0).map(({ value }) => (
              <Box key={value} style={{ width: `${(noticeCounts[value] / countedPoints) * 100}%`, backgroundColor: palette.status[value] }} />
            ))}
          </Box>
        )}
      </Box>
      {(segment.showCounts ?? true) && legend}
    </Box>
  );
};

// ---- Checkpoints ----

/** A step's comment where the segment prints comments. */
const commentOf = (step: SampleStep, segment: ReportCheckpointsSegment): string =>
  ((segment.showComments ?? true) ? step.comment : '');

/** A yes or a no, drawn as the report draws it: its font has no glyph for either. */
const Mark: React.FC<{ on: boolean }> = ({ on }) => (
  <Box component="svg" viewBox="0 0 24 24" aria-hidden sx={answerStyles.mark}>
    <path
      d={on ? 'M5 12.5l4.5 4.5L19 7.5' : 'M6.5 6.5l11 11M17.5 6.5l-11 11'}
      stroke="#222222" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" fill="none"
    />
  </Box>
);

/**
 * The answers to a step's POI type, as one line: the type's name, then each label with its
 * answer. The report prints a table of them; a line is enough to show that they are there
 * and to see them go when the design switches them off.
 */
const AnswersLine: React.FC<{ answers: NonNullable<SampleStep['answers']>; density: 'compact' | 'regular'; print: Print }> = ({
  answers, density, print,
}) => (
  <Box sx={answerStyles[density]}>
    <Box component="span" sx={answerStyles.name}>{print.label(answers.name)}</Box>
    {answers.fields.map(({ label, value }, index) => (
      <Fragment key={index}>
        {` · ${print.label(label)}: `}
        <Box component="span" sx={answerStyles.value}>
          {typeof value === 'boolean' && <Mark on={value} />}
          {typeof value === 'number' && print.formatNumber(value)}
          {typeof value === 'string' && value}
          {typeof value === 'object' && print.label(value)}
        </Box>
      </Fragment>
    ))}
  </Box>
);

/**
 * One point as a card (`PointCard`): its name and status, then each step with its answers and
 * its photos as thumbnails. In the grid a card prints neither photos nor answers, whatever
 * the segment asks for.
 */
const PointCard: React.FC<{ point: SamplePoint; segment: ReportCheckpointsSegment; inGrid: boolean; print: Print }> = ({
  point, segment, inGrid, print,
}) => {
  const notice = pointNotice(point);
  const showThumbs = !inGrid && (segment.photos ?? 'thumbs') === 'thumbs';
  const showAnswers = !inGrid && segment.showCustomFields !== false;

  return (
    <Box sx={inGrid ? styles.gridCard : styles.card} style={{ borderLeftColor: palette.status[notice] }}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardNumber}>{numberOf(point)}</Box>
        <Box sx={styles.cardName}>{point.name}</Box>
        <StatusPill notice={notice} print={print} />
      </Box>

      {point.steps.map((step, stepIndex) => {
        const comment = commentOf(step, segment);
        const photos = showThumbs ? step.photos : [];
        const followed = stepIndex < point.steps.length - 1;

        return (
          <Fragment key={stepIndex}>
            <Box sx={styles.stepRow}>
              <Box sx={styles.stepNumberColumn}>
                <Box sx={styles.stepNumber}>{`${point.number}.${stepIndex + 1}`}</Box>
              </Box>
              <Box sx={styles.stepBody}>
                {step.description && <Box sx={styles.stepText}>{step.description}</Box>}
                {comment && <Box sx={styles.stepComment}>{comment}</Box>}
              </Box>
              <Box sx={styles.stepDotColumn}>
                <Box sx={styles.stepDot} style={{ backgroundColor: palette.status[step.notice] }} />
              </Box>
            </Box>
            {showAnswers && step.answers && (
              <Box sx={styles.stepAnswers}><AnswersLine answers={step.answers} density="compact" print={print} /></Box>
            )}
            {photos.length > 0 && (
              <Box sx={followed ? [styles.thumbRow, styles.thumbRowFollowed] : styles.thumbRow}>
                {photos.map((photo) => <Box key={photo.name} component="img" alt="" draggable={false} src={photo.picture} sx={styles.thumb} />)}
              </Box>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};

/**
 * One point as the page it gets to itself (`PointPage`): each step with its description, its
 * comment, its control date, its answers and its photos at full width. It reads neither the
 * title nor the switches for descriptions and dates, which are the table's.
 */
const PointDetail: React.FC<{ point: SamplePoint; segment: ReportCheckpointsSegment; print: Print }> = ({
  point, segment, print,
}) => {
  const { t } = print;

  const line = (label: string, value: string) => (
    <Box sx={styles.detailRow}>
      <Box sx={styles.detailLabelColumn}><Box sx={styles.detailLabel}>{label}</Box></Box>
      <Box sx={styles.detailValue}>{value}</Box>
    </Box>
  );

  return (
    <Box sx={viewStyles}>
      <Box sx={styles.detailHeader}>
        <Box sx={styles.detailTitle}>{`${numberOf(point)}  ${point.name}`}</Box>
        <StatusPill notice={pointNotice(point)} print={print} />
      </Box>
      {point.steps.map((step, stepIndex) => {
        const comment = commentOf(step, segment);

        return (
          <Box key={stepIndex} sx={stepIndex > 0 ? [viewStyles, styles.detailStep] : viewStyles}>
            {step.description && line(t('Common_Description'), step.description)}
            {comment && line(t('Common_Message'), comment)}
            {line(t('Report_Control_Date'), print.formatDateTime(step.modified))}
            {segment.showCustomFields !== false && step.answers && (
              <AnswersLine answers={step.answers} density="regular" print={print} />
            )}
            {(segment.photos === 'none' ? [] : step.photos).map((photo) => (
              <Fragment key={photo.name}>
                <Box component="img" alt="" draggable={false} src={photo.picture} sx={styles.detailPhoto} />
                <Box sx={styles.detailPhotoCaption}>{`${photo.name} • ${print.formatDateTime(photo.taken)}`}</Box>
              </Fragment>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

/** The points a design lists: the checkpoints, then the notes when it asks for them, narrowed to its statuses. */
const listedPoints = (segment: ReportCheckpointsSegment, { sample }: Print): SamplePoint[] =>
  [...sample.checkpoints, ...(segment.includeNotes ? sample.notes : [])]
    .filter((point) => !segment.statuses?.length || segment.statuses.includes(pointNotice(point)));

/**
 * The checkpoints in their template (`PointList`, and `PointPage` for `detailed`), with what
 * the segment switches on and off. Nothing for `none`.
 *
 * `detailed` gives every point a page of its own, so the canvas, which shows the first page of
 * the results, draws the first point under a line that says so. The sample's points are all
 * short, so the grid is drawn as pairs: the row a long point takes to itself does not come up.
 */
export const CheckpointsView: React.FC<{ segment: ReportCheckpointsSegment; print: Print }> = ({ segment, print }) => {
  if (segment.template === 'none') return null;

  const { t } = print;
  const points = listedPoints(segment, print);

  if (segment.template === 'detailed') {
    return (
      <>
        <Box sx={pageBreakStyles}>{t('ReportDesign_Template_Detailed')}</Box>
        {points.length > 0 && <PointDetail point={points[0]} segment={segment} print={print} />}
      </>
    );
  }

  const title = <Box sx={styles.sectionTitle}>{capitals(print.text(segment.title) ?? print.translate('Inspection_Checkpoints'))}</Box>;

  if (segment.template === 'table') {
    const showDescriptions = segment.showDescriptions ?? false;
    const showDates = segment.showDates ?? false;
    // The name takes what the number, the status and the columns asked for leave of the row.
    const nameColumn = { width: `${100 - 8 - 20 - (showDescriptions ? 30 : 0) - (showDates ? 16 : 0)}%` };

    return (
      <>
        {title}
        <Box sx={styles.tableHeader}>
          <Box sx={[styles.tableHeaderText, styles.numberColumn]}>#</Box>
          <Box sx={styles.tableHeaderText} style={nameColumn}>{capitals(print.translate('Common_Title'))}</Box>
          {showDescriptions && <Box sx={[styles.tableHeaderText, styles.descriptionColumn]}>{capitals(print.translate('Common_Description'))}</Box>}
          <Box sx={[styles.tableHeaderText, styles.statusColumn]}>{capitals(print.translate('Common_Status'))}</Box>
          {showDates && <Box sx={[styles.tableHeaderText, styles.dateColumn]}>{capitals(print.translate('Report_Control_Date'))}</Box>}
        </Box>
        {points.map((point) => (
          <Box key={point.number} sx={styles.tableRow}>
            <Box sx={[styles.cellMuted, styles.numberColumn]}>{numberOf(point)}</Box>
            <Box sx={[styles.cellText, styles.cellGap]} style={nameColumn}>{point.name}</Box>
            {showDescriptions && <Box sx={[styles.cellMuted, styles.cellGap, styles.descriptionColumn]}>{point.steps[0]?.description}</Box>}
            <Box sx={styles.statusColumn}><Box sx={{ display: 'flex' }}><StatusPill notice={pointNotice(point)} print={print} /></Box></Box>
            {showDates && <Box sx={[styles.cellMuted, styles.dateColumn]}>{point.steps[0] ? print.formatDate(point.steps[0].modified) : ''}</Box>}
          </Box>
        ))}
      </>
    );
  }

  if (segment.template === 'grid') {
    const rows = points.flatMap((_point, index) => (index % 2 === 0 ? [points.slice(index, index + 2)] : []));

    return (
      <>
        {title}
        {rows.map((pair, index) => (
          <Fragment key={pair[0].number}>
            {index > 0 && <Box sx={styles.gridGap} />}
            <Box sx={styles.gridRow}>
              {pair.map((point) => <PointCard key={point.number} point={point} segment={segment} inGrid print={print} />)}
            </Box>
          </Fragment>
        ))}
      </>
    );
  }

  return (
    <>
      {title}
      {points.map((point, index) => (
        <Fragment key={point.number}>
          {index > 0 && <Box sx={styles.cardGap} />}
          <PointCard point={point} segment={segment} inGrid={false} print={print} />
        </Fragment>
      ))}
    </>
  );
};
